import { setTradeableTokensForceFetchInProgress } from '@/components/providers/postauth/TradeableTokensProvider';
import { setTransactionsForceFetchInProgress } from '@/components/providers/postauth/UserTransactionsProvider';
import { setWalletsForceFetchInProgress } from '@/components/providers/postauth/UserWalletsProvider';
import ENDPOINTS from '@/config/apiEndpoints';
import ApiService from '@/services/ApiService';
import AuthorizationService from '@/services/AuthorizationService';
import reactQueryClient from '@/services/ReactQueryClientService';
import { logDebug, logError, logInfo } from '@/utils/logger';
export interface ReferralData {
  referralCode: string;
  userId: string;
  referredUsers: string[];
  hasUsedReferralCode: boolean;
  referredBy?: string;
  createdAt: string;
  updatedAt: string;
}
export interface ReferralCodeResponse {
  success: boolean;
  errorType?: string;
  authorizationChanged?: boolean;
}
interface ApiErrorResponse {
  error?: string;
  errorCode?: string;
}
const setAllForceFetchStates = (state: boolean): void => {
  logInfo(
    `Setting all force fetch states to ${state}`,
    'ReferralService.setAllForceFetchStates',
  );
  setWalletsForceFetchInProgress(state);
  setTransactionsForceFetchInProgress(state);
  setTradeableTokensForceFetchInProgress(state);
};
const refreshAllData = async (): Promise<void> => {
  try {
    logInfo('Step 1: Refreshing wallets', 'ReferralService.refreshAllData');
    await reactQueryClient.invalidateQueries({ queryKey: ['wallets'] });
    await reactQueryClient.refetchQueries({
      queryKey: ['wallets'],
      exact: true,
    });
    logInfo(
      'Step 1: Wallets refreshed successfully',
      'ReferralService.refreshAllData',
    );
    logInfo(
      'Waiting 1 second before refreshing transactions...',
      'ReferralService.refreshAllData',
    );
    await new Promise((resolve) => setTimeout(resolve, 100));
    logInfo(
      'Delay complete, proceeding with transactions refresh.',
      'ReferralService.refreshAllData',
    );
    logInfo(
      'Step 2: Refreshing transactions',
      'ReferralService.refreshAllData',
    );
    await reactQueryClient.invalidateQueries({ queryKey: ['transactions'] });
    await reactQueryClient.refetchQueries({
      queryKey: ['transactions'],
      exact: true,
    });
    logInfo(
      'Step 2: Transactions refreshed successfully',
      'ReferralService.refreshAllData',
    );
    logInfo(
      'Step 3: Refreshing tradeable tokens',
      'ReferralService.refreshAllData',
    );
    await reactQueryClient.invalidateQueries({ queryKey: ['tradeableTokens'] });
    await reactQueryClient.refetchQueries({
      queryKey: ['tradeableTokens'],
      exact: true,
    });
    logInfo(
      'Step 3: Tradeable tokens refreshed successfully',
      'ReferralService.refreshAllData',
    );
    logInfo(
      'Successfully refreshed all data sequentially',
      'ReferralService.refreshAllData',
    );
  } catch (error) {
    logError(error, 'ReferralService.refreshAllData', {
      message: 'Failed to refresh data',
    });
  }
};
export const getReferralData = async (): Promise<ReferralData | null> => {
  try {
    logInfo('Getting referral data', 'ReferralService.getReferralData');
    const response = await ApiService.get(ENDPOINTS.USER_REFERRAL_GET);
    logInfo(
      'Referral data fetched successfully',
      'ReferralService.getReferralData',
      { response },
    );
    return response;
  } catch (error) {
    logError(error, 'ReferralService.getReferralData');
    return null;
  }
};
export const createReferralCode = async (): Promise<string> => {
  try {
    logInfo(
      'Creating referral code - start',
      'ReferralService.createReferralCode',
    );
    logDebug(
      'About to call ApiService.post',
      'ReferralService.createReferralCode',
      {
        endpoint: ENDPOINTS.USER_REFERRAL_CREATE,
        payload: {},
      },
    );
    const response = await ApiService.post(ENDPOINTS.USER_REFERRAL_CREATE, {});
    logInfo(
      'ApiService.post response received',
      'ReferralService.createReferralCode',
      { response },
    );
    if (response && typeof response === 'object' && 'error' in response) {
      logError(
        'API returned an error response',
        'ReferralService.createReferralCode',
        { response },
      );
      throw new Error('Failed to create referral code');
    }
    logInfo(
      'Referral code created successfully',
      'ReferralService.createReferralCode',
      { code: response.code },
    );
    return response.code;
  } catch (error) {
    const typedError = error as Error;
    logError(typedError, 'ReferralService.createReferralCode', {
      errorMessage: typedError.message,
      errorStack: typedError.stack,
    });
    throw error;
  }
};
const checkAuthorizationWithRetries = async (
  code: string,
): Promise<boolean> => {
  const maxRetries = 3;
  let authorizationChanged = false;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    logInfo(
      `Authorization check attempt ${attempt + 1}/${maxRetries}`,
      'ReferralService.checkAuthorizationWithRetries',
      { code, attempt: attempt + 1 },
    );
    authorizationChanged =
      await AuthorizationService.checkAuthorizationStatus();
    logInfo(
      `Authorization status ${
        authorizationChanged ? 'changed' : 'unchanged'
      } on attempt ${attempt + 1}`,
      'ReferralService.checkAuthorizationWithRetries',
      { code, authorizationChanged, attempt: attempt + 1 },
    );
    if (authorizationChanged) {
      logInfo(
        'Authorization status changed, no more retries needed',
        'ReferralService.checkAuthorizationWithRetries',
        { code, attempt: attempt + 1 },
      );
      break;
    }
    if (attempt < maxRetries - 1) {
      const delayMs = 5000 * (attempt + 1);
      logInfo(
        `Authorization status unchanged, waiting ${
          delayMs / 1000
        }s before next attempt`,
        'ReferralService.checkAuthorizationWithRetries',
        { code, attempt: attempt + 1 },
      );
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return authorizationChanged;
};
const handleApiError = (error: unknown, code: string): ReferralCodeResponse => {
  const typedError = error as {
    message?: string;
    status?: number;
    stack?: string;
  };
  if (
    typedError.message &&
    (typedError.message.includes('Network Error') ||
      typedError.message.includes('Failed to fetch'))
  ) {
    logError('Network error', 'ReferralService.applyReferralCode', {
      code,
      errorMessage: typedError.message,
    });
    return { success: false, errorType: 'network' };
  }
  if (typedError.status) {
    if (typedError.status >= 500) {
      logError('Server error', 'ReferralService.applyReferralCode', {
        code,
        status: typedError.status,
        errorMessage: typedError.message,
      });
      return { success: false, errorType: 'server' };
    }
    if (typedError.status === 401 || typedError.status === 403) {
      logError('Authentication error', 'ReferralService.applyReferralCode', {
        code,
        status: typedError.status,
        errorMessage: typedError.message,
      });
      return { success: false, errorType: 'auth' };
    }
    if (typedError.status === 400) {
      logError('Invalid input error', 'ReferralService.applyReferralCode', {
        code,
        status: typedError.status,
        errorMessage: typedError.message,
      });
      return { success: false, errorType: 'invalid' };
    }
  }
  logError('Unknown error', 'ReferralService.applyReferralCode', {
    code,
    errorMessage: typedError.message,
    errorStack: typedError.stack,
  });
  return { success: false, errorType: 'unknown' };
};
export const applyReferralCode = async (
  code: string,
  skipRefresh = false,
): Promise<ReferralCodeResponse> => {
  try {
    logInfo(
      'Applying referral code - start',
      'ReferralService.applyReferralCode',
      { code, skipRefresh },
    );
    logDebug(
      'About to call ApiService.post',
      'ReferralService.applyReferralCode',
      { endpoint: ENDPOINTS.USER_REFERRAL_APPLY, payload: { code } },
    );
    const response = await ApiService.post(ENDPOINTS.USER_REFERRAL_APPLY, {
      code,
    });
    logInfo(
      'ApiService response received',
      'ReferralService.applyReferralCode',
      { response, code },
    );
    if (response && typeof response === 'object') {
      const apiResponse = response as ApiErrorResponse;
      if ('error' in apiResponse) {
        logError(
          'API returned an error response',
          'ReferralService.applyReferralCode',
          { response, code },
        );
        setAllForceFetchStates(false);
        return { success: false, errorType: 'api' };
      }
      if ('errorCode' in apiResponse) {
        const errorCode = apiResponse.errorCode || '';
        logInfo(
          'API returned an error code',
          'ReferralService.applyReferralCode',
          { errorCode, code },
        );
        setAllForceFetchStates(false);
        if (errorCode === 'CODE_ALREADY_USED') {
          return { success: false, errorType: 'already_used' };
        }
        return { success: false, errorType: 'api' };
      }
    }
    logInfo(
      'Referral code applied successfully',
      'ReferralService.applyReferralCode',
      { code },
    );
    if (skipRefresh) {
      logInfo(
        'Skipping authorization check and data refresh as requested',
        'ReferralService.applyReferralCode',
        { code },
      );
      return { success: true, authorizationChanged: false };
    }
    setAllForceFetchStates(true);
    const result = { success: true, authorizationChanged: false };
    (async () => {
      try {
        logInfo(
          'Starting authorization status check with retries in background',
          'ReferralService.applyReferralCode.background',
          { code },
        );
        const authorizationChanged = await checkAuthorizationWithRetries(code);
        if (
          authorizationChanged &&
          AuthorizationService.getAuthorizationStatus()
        ) {
          try {
            logInfo(
              'Authorization status changed to authorized, refreshing data',
              'ReferralService.applyReferralCode.background',
              { code },
            );
            await refreshAllData();
          } catch (refreshError) {
            logError(
              refreshError,
              'ReferralService.applyReferralCode.background.refreshData',
              {
                code,
                message: 'Failed to refresh data after authorization change',
              },
            );
          } finally {
            setTimeout(() => setAllForceFetchStates(false), 1000);
          }
        } else {
          setTimeout(() => setAllForceFetchStates(false), 1000);
        }
      } catch (backgroundError) {
        logError(
          backgroundError,
          'ReferralService.applyReferralCode.background',
          { code, message: 'Error in background process' },
        );
        setAllForceFetchStates(false);
      }
    })();
    return result;
  } catch (error) {
    setAllForceFetchStates(false);
    return handleApiError(error, code);
  }
};
const ReferralService = {
  getReferralData,
  createReferralCode,
  applyReferralCode,
};
export default ReferralService;
